/**
 * Shared Document Service Layer
 * Provides a unified interface for document operations across DMS and EdgeDocs
 */

import { 
  DocumentDetail, 
  DocumentListItem, 
  DocumentVersion, 
  DocumentSaveRequest, 
  DocumentLoadRequest,
  DocumentExportRequest,
  DocumentImportRequest,
  EditorDocumentState 
} from './types';

/**
 * Document Service Interface
 * Defines the contract for document operations
 */
export interface IDocumentService {
  // Core CRUD operations
  getDocumentById(id: string): Promise<DocumentDetail>;
  getDocumentList(filters?: Record<string, any>): Promise<DocumentListItem[]>;
  saveDocument(request: DocumentSaveRequest): Promise<DocumentDetail>;
  deleteDocument(id: string): Promise<void>;
  
  // Version management
  getDocumentVersions(documentId: string): Promise<DocumentVersion[]>;
  loadDocumentVersion(request: DocumentLoadRequest): Promise<DocumentDetail>;
  
  // Content operations
  getLexicalContent(documentId: string): Promise<string>;
  saveLexicalContent(documentId: string, content: string, metadata?: Record<string, any>): Promise<void>;
  
  // Import/Export
  importDocument(request: DocumentImportRequest): Promise<DocumentDetail>;
  exportDocument(request: DocumentExportRequest): Promise<Blob>;
  
  // Editor state management
  getEditorState(documentId: string): Promise<EditorDocumentState>;
  saveEditorState(documentId: string, state: EditorDocumentState): Promise<void>;
}

/**
 * EdgeLex Document Service Implementation
 * Handles all document operations through the DMS API
 */
class EdgeLexDocumentService implements IDocumentService {
  private baseUrl = '/api/v1/dms/documents';
  
  async getDocumentById(id: string): Promise<DocumentDetail> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async getDocumentList(filters?: Record<string, any>): Promise<DocumentListItem[]> {
    const queryParams = filters ? new URLSearchParams(filters).toString() : '';
    const url = queryParams ? `${this.baseUrl}?${queryParams}` : this.baseUrl;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch document list: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async saveDocument(request: DocumentSaveRequest): Promise<DocumentDetail> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to save document: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async deleteDocument(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.statusText}`);
    }
  }
  
  async getDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
    const response = await fetch(`${this.baseUrl}/${documentId}/versions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch document versions: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async loadDocumentVersion(request: DocumentLoadRequest): Promise<DocumentDetail> {
    const url = request.version 
      ? `${this.baseUrl}/${request.documentId}/versions/${request.version}`
      : `${this.baseUrl}/${request.documentId}`;
      
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to load document version: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async getLexicalContent(documentId: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/${documentId}/lexical-content`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch lexical content: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.content || '';
  }
  
  async saveLexicalContent(documentId: string, content: string, metadata?: Record<string, any>): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${documentId}/lexical-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ content, metadata }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to save lexical content: ${response.statusText}`);
    }
  }
  
  async importDocument(request: DocumentImportRequest): Promise<DocumentDetail> {
    const formData = new FormData();
    formData.append('file', request.file);
    
    if (request.matterId) {
      formData.append('matterId', request.matterId);
    }
    
    if (request.clientId) {
      formData.append('clientId', request.clientId);
    }
    
    if (request.metadata) {
      formData.append('metadata', JSON.stringify(request.metadata));
    }
    
    const response = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to import document: ${response.statusText}`);
    }
    
    return response.json();
  }
  
  async exportDocument(request: DocumentExportRequest): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/${request.documentId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to export document: ${response.statusText}`);
    }
    
    return response.blob();
  }
  
  async getEditorState(_documentId: string): Promise<EditorDocumentState> {
    // For now, return a default state - this could be enhanced to load from localStorage or backend
    return {
      content: '',
      wordCount: 0,
      characterCount: 0,
      pageCount: 1,
      lastSaved: null,
      hasUnsavedChanges: false,
      isProcessing: false,
      selectedTemplate: null,
      documentMetadata: {},
    };
  }
  
  async saveEditorState(documentId: string, state: EditorDocumentState): Promise<void> {
    // For now, save to localStorage - this could be enhanced to save to backend
    localStorage.setItem(`editor_state_${documentId}`, JSON.stringify(state));
  }
}

// Export singleton instance
export const DocumentService = new EdgeLexDocumentService(); 